import { Breadcrumb, CoreButton, CoreModalDialog } from '@components'
import ConfirmationModal from '@components/ConfirmationModal'
import EditReportNameIcon from '@images/expenses/edit-report-name.svg?react'
import ArchiveIcon from '@images/icons/archive-icon.svg?react'
import LeftArrowIcon from '@images/icons/left-arrow-icon.svg?react'
import { toastFailure, toastSuccess } from '@utils'
import {
  paths,
  useArchiveCounterpartyMutation,
  useGetCounterpartyQuery,
  useRestoreCounterpartyMutation
} from 'mmfintech-backend-api'
import { GlobalContext, tr } from 'mmfintech-commons'
import { useContext, useMemo, useState } from 'react'
import { CounterpartyCreateModal } from '../CounterpartyCreateModal'

import './counterpartyPageHeader.scss'

interface CounterpartyPageHeaderProps {
  counterpartyId: string
}

const BREADCRUMB_ITEMS: { label: string; path: (id?: string) => string }[] = [
  {
    get label() {
      return tr('FRONTEND.HEADER.REPORTS', 'Reports')
    },
    path: () => paths.expenseManagement.dashboard()
  },
  {
    get label() {
      return tr('FRONTEND.HEADER.COUNTERPARTY', 'Counterparties')
    },
    path: () => paths.expenseManagement.counterparties()
  },
  {
    get label() {
      return tr('FRONTEND.COUNTERPARTY_PAGE_HEADER.COUNTERPARTIES_DETAIL', 'Counterparties Detail')
    },
    path: (id: string) => paths.expenseManagement.counterpartyDetails(id)
  }
]

export const CounterpartyPageHeader = ({ counterpartyId }: CounterpartyPageHeaderProps) => {
  const { modalHide, modalShow } = useContext(GlobalContext)

  const [showEditModal, setShowEditModal] = useState(false)

  const { data: counterparty } = useGetCounterpartyQuery({
    counterpartyId: Number(counterpartyId)
  })

  const [archive, { error: archiveError }] = useArchiveCounterpartyMutation()
  const [restore, { error: restoreError }] = useRestoreCounterpartyMutation()

  const isArchived = useMemo(() => counterparty?.state === 'ARCHIVED', [counterparty])
  const text = isArchived
    ? tr('FRONTEND.COUNTERPARTY.RESTORE', 'Restore')
    : tr('FRONTEND.COUNTERPARTY.ARCHIVE', 'Archive')

  const question = isArchived
    ? tr('FRONTEND.COUNTERPARTY.QUESTION.RESTORE', 'Are you sure you want to restore this counterparty?')
    : tr('FRONTEND.COUNTERPARTY.QUESTION.ARCHIVE', 'Are you sure you want to archive this counterparty?')

  const handleArchiveRestore = async () => {
    try {
      isArchived
        ? await restore({ counterpartyId: counterparty.id }).unwrap()
        : await archive({ counterpartyId: counterparty.id }).unwrap()

      toastSuccess(`Counterparty ${isArchived ? 'restored' : 'archived'} successfully`)
      modalHide()
    } catch (error) {
      toastFailure(error.error)
    }
  }

  const handleEditModalOpen = () => setShowEditModal(true)
  const handleEditModalClose = () => setShowEditModal(false)

  const handleConfirmArchiveModalOpen = () => {
    modalShow({
      header: text,
      content: (
        <ConfirmationModal question={question} onConfirm={handleArchiveRestore} error={[archiveError, restoreError]} />
      ),
      options: {}
    })
  }

  return !counterparty?.state ? null : (
    <div className='counterparty-header-wrapper'>
      <div className='counterparty-header-container'>
        <div className='header-left-side'>
          <div
            onClick={e => {
              e.preventDefault()
              window.history.back()
            }}
            className='back-arrow-icon'
          >
            <LeftArrowIcon />
          </div>
          <h1 className='label'>{counterparty?.name}</h1>
        </div>
        <div className='actions-counterparty'>
          <CoreButton
            data-test='open-edit-counterparty-modal'
            onClick={handleEditModalOpen}
            CollapsedIcon={<EditReportNameIcon />}
            collapsed
            variation='elevated'
            size='large'
          />

          <CoreButton
            variation='elevated'
            text={text}
            LeftIcon={<ArchiveIcon />}
            data-test='archive-btn'
            onClick={handleConfirmArchiveModalOpen}
            size='large'
          />
        </div>

        <CoreModalDialog
          header={`${tr('FRONTEND.COUNTERPARTY.ACTION.NEW', 'New')} ${tr(
            'FRONTEND.EXPENSE_MANAGEMENT.TITLE.COUNTERPARTY',
            'Counterparty'
          )}`}
          options={{ closeOnEscape: false, closeOnClickOutside: false }}
          visible={showEditModal}
          content={<CounterpartyCreateModal onClose={handleEditModalClose} counterparty={counterparty} />}
          onClose={handleEditModalClose}
        />
      </div>
      <Breadcrumb items={BREADCRUMB_ITEMS} />
    </div>
  )
}
